import React from 'react';

export interface BatchOption {
  batch_id: string;
  batch_name: string;
  batch_time: string;
}

interface BatchSelectorProps {
  batches: BatchOption[];
  selectedBatchId: string | null;
  onSelect: (batchId: string) => void;
}

// Compact batch picker: a responsive grid of fixed, truncated chips instead
// of full-width pills that stretch to the length of the longest batch name
// (which, with real course titles, made each pill span the whole row).
// Full names are still available via the native `title` tooltip on hover.
const BatchSelector: React.FC<BatchSelectorProps> = ({ batches, selectedBatchId, onSelect }) => {
  return (
    <div
      style={{
        background: 'white',
        borderRadius: '14px',
        padding: '14px 18px',
        boxShadow: '0 1px 4px rgba(14,34,93,0.06)',
        border: '1px solid #E5E7EB',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
        <div style={{ width: '4px', height: '18px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '4px' }} />
        <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#0e225d', letterSpacing: '0.3px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <i className="pi pi-th-large" style={{ fontSize: '0.8rem' }}></i>
          Select Batch
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: '8px',
        }}
      >
        {batches.map((batch) => {
          const isSelected = selectedBatchId === batch.batch_id;
          return (
            <div
              key={batch.batch_id}
              onClick={() => onSelect(batch.batch_id)}
              title={batch.batch_name}
              style={{
                padding: '8px 12px',
                background: isSelected ? 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)' : 'white',
                border: isSelected ? '1.5px solid transparent' : '1.5px solid #E5E7EB',
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                minWidth: 0,
                boxShadow: isSelected ? '0 3px 10px rgba(14,34,93,0.25)' : 'none',
              }}
            >
              <div
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: isSelected ? '#fbb21f' : 'rgba(14,34,93,0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <i className="pi pi-users" style={{ fontSize: '0.62rem', color: '#0e225d' }}></i>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0, flex: 1 }}>
                <span
                  style={{
                    fontSize: '0.68rem',
                    fontWeight: '600',
                    color: isSelected ? 'white' : '#0e225d',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {batch.batch_name}
                </span>
                <span style={{ fontSize: '0.58rem', color: isSelected ? 'rgba(255,255,255,0.65)' : '#616366' }}>
                  {batch.batch_time}
                </span>
              </div>

              {isSelected && <i className="pi pi-check" style={{ fontSize: '0.68rem', color: '#fbb21f', flexShrink: 0 }}></i>}
            </div>
          );
        })}

        {batches.length === 0 && (
          <div style={{ padding: '40px 20px', textAlign: 'center', gridColumn: '1 / -1' }}>
            <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.9rem' }}>No active batches found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BatchSelector;
